<template>
  <section
    v-if="hasScript"
    class="guided-autocue"
    :class="[alignmentClass]"
    :aria-label="$t('routes.guidedRecording.autocue.heading')"
    data-cy="guidedRecording-autocue"
  >
    <div class="guided-autocue__header">
      <h2 class="guided-autocue__heading text-h6 mb-0">
        {{ $t('routes.guidedRecording.autocue.heading') }}
      </h2>
      <div class="guided-autocue__header-actions">
        <div v-show="visible" class="guided-autocue__lifecycle">
          <v-small-icon-btn
            v-if="scrollState === 'stopped'"
            color="primary"
            :disabled="reducedMotion"
            :aria-label="$t('routes.guidedRecording.autocue.start')"
            data-cy="guidedRecording-autocue-start"
            @click="startScrolling"
          >
            <v-icon aria-hidden="true">mdi-play</v-icon>
            <v-tooltip activator="parent" location="bottom">
              {{ $t('routes.guidedRecording.autocue.start') }}
            </v-tooltip>
          </v-small-icon-btn>
          <v-small-icon-btn
            v-else-if="scrollState === 'scrolling'"
            color="primary"
            :aria-label="$t('routes.guidedRecording.autocue.pause')"
            data-cy="guidedRecording-autocue-pause"
            @click="pauseScrolling"
          >
            <v-icon aria-hidden="true">mdi-pause</v-icon>
            <v-tooltip activator="parent" location="bottom">
              {{ $t('routes.guidedRecording.autocue.pause') }}
            </v-tooltip>
          </v-small-icon-btn>
          <v-small-icon-btn
            v-else
            :disabled="reducedMotion"
            :aria-label="$t('routes.guidedRecording.autocue.resume')"
            data-cy="guidedRecording-autocue-resume"
            @click="resumeScrolling"
          >
            <v-icon aria-hidden="true">mdi-play</v-icon>
            <v-tooltip activator="parent" location="bottom">
              {{ $t('routes.guidedRecording.autocue.resume') }}
            </v-tooltip>
          </v-small-icon-btn>
          <v-small-icon-btn
            :aria-label="$t('routes.guidedRecording.autocue.restart')"
            data-cy="guidedRecording-autocue-restart"
            @click="restartScrolling"
          >
            <v-icon aria-hidden="true">mdi-restart</v-icon>
            <v-tooltip activator="parent" location="bottom">
              {{ $t('routes.guidedRecording.autocue.restart') }}
            </v-tooltip>
          </v-small-icon-btn>
        </div>
        <GuidedAutocueSettingsDialog
          v-model="settingsOpen"
          :alignment="selectedAlignment"
          :font-size="selectedFontSize"
          :scroll-speed="selectedScrollSpeed"
          @update:alignment="setAlignment"
          @update:font-size="setFontSize"
          @update:scroll-speed="setScrollSpeed"
        >
          <template #activator="{ props: activatorProps }">
            <v-small-icon-btn
              v-show="visible"
              v-bind="activatorProps"
              :aria-label="$t('routes.guidedRecording.autocue.settings')"
              data-cy="guidedRecording-autocue-settings"
            >
              <v-icon aria-hidden="true">mdi-cog</v-icon>
              <v-tooltip activator="parent" location="bottom">
                {{ $t('routes.guidedRecording.autocue.settings') }}
              </v-tooltip>
            </v-small-icon-btn>
          </template>
        </GuidedAutocueSettingsDialog>
        <v-small-icon-btn
          :aria-pressed="visible"
          :aria-label="$t(visible
            ? 'routes.guidedRecording.autocue.toggleHide'
            : 'routes.guidedRecording.autocue.toggleShow')"
          data-cy="guidedRecording-autocue-toggle"
          @click="toggleVisibility"
        >
          <v-icon aria-hidden="true">{{ visible ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
        </v-small-icon-btn>
      </div>
    </div>

    <div
      v-show="visible"
      ref="scriptScroller"
      class="guided-autocue__script"
      :style="fontSizeStyle"
      dir="auto"
      data-cy="guidedRecording-autocue-script"
      aria-live="off"
      @scroll="syncScrollPosition"
    >
      <p class="guided-autocue__script-text mb-0" dir="auto">{{ script }}</p>
    </div>

  </section>
</template>

<script>
import GuidedAutocueSettingsDialog from '@/routes/guided-recording/components/GuidedAutocueSettingsDialog.vue';

const FONT_SIZE_MIN = 14;
const FONT_SIZE_MAX = 48;
const MAX_FRAME_DELTA_SECONDS = 0.1;
const SCROLL_START_DELAY_MS = 3000;
const ALIGNMENT_VALUES = ['left', 'center', 'right'];
const SCROLL_SPEED_MIN = 1;
const SCROLL_SPEED_MAX = 80;
const SCROLL_SPEED_DEFAULT = 40;
const AUTOCUE_TELEMETRY_REASONS = Object.freeze({
  autocue_open: Object.freeze(['opened', 'hidden']),
  autocue_alignment: Object.freeze(['left', 'center', 'right']),
  autocue_control_failure: Object.freeze([
    'start',
    'pause',
    'resume',
    'restart',
    'no_script',
    'reduced_motion',
    'missing_scroller',
  ]),
});

const readMonotonicTime = () => {
  const clockValue = typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

  return Number.isFinite(clockValue) ? clockValue : 0;
};

const getReducedMotionQuery = () => (
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null
);

const isAlignment = (value) => ALIGNMENT_VALUES.includes(value);

const isScrollSpeed = (value) => Number.isInteger(value) && value >= SCROLL_SPEED_MIN && value <= SCROLL_SPEED_MAX;

const clampFontSize = (value) => {
  const size = Number(value);
  if (!Number.isFinite(size)) {
    return FONT_SIZE_MIN;
  }

  return Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(size)));
};

const clampScrollSpeed = (value) => (
  Number.isFinite(value)
    ? Math.min(SCROLL_SPEED_MAX, Math.max(SCROLL_SPEED_MIN, Math.round(value)))
    : SCROLL_SPEED_DEFAULT
);

export default {
  name: 'GuidedAutocue',
  components: { GuidedAutocueSettingsDialog },
  emits: ['telemetry', 'update:alignment', 'update:fontSize', 'update:scrollSpeed'],
  props: {
    alignment: {
      type: String,
      default: 'center',
      validator: isAlignment,
    },
    fontSize: {
      type: Number,
      default: 24,
      validator: value => Number.isFinite(value),
    },
    script: { type: String, default: '' },
    elapsedSeconds: { type: Number, default: 0 },
    remainingSeconds: { type: Number, default: 0 },
    isRecording: { type: Boolean, default: false },
    scrollSpeed: {
      type: Number,
      default: SCROLL_SPEED_DEFAULT,
      validator: isScrollSpeed,
    },
  },
  data() {
    return {
      lastTickElapsed: 0,
      reducedMotion: false,
      reducedMotionQuery: null,
      scrollAnimationFrame: null,
      scrollPosition: 0,
      scrollState: 'stopped',
      selectedAlignment: this.alignment,
      selectedFontSize: clampFontSize(this.fontSize),
      selectedScrollSpeed: clampScrollSpeed(this.scrollSpeed),
      settingsOpen: false,
      visible: true,
    };
  },
  computed: {
    hasScript() {
      return typeof this.script === 'string' && this.script.trim().length > 0;
    },
    alignmentClass() {
      return `guided-autocue--align-${this.selectedAlignment}`;
    },
    fontSizeStyle() {
      return { fontSize: `${this.selectedFontSize}px` };
    },
  },
  watch: {
    alignment(alignmentValue) {
      if (isAlignment(alignmentValue)) {
        this.selectedAlignment = alignmentValue;
      }
    },
    fontSize(fontSizeValue) {
      this.selectedFontSize = clampFontSize(fontSizeValue);
    },
    isRecording(isRecordingValue) {
      if (isRecordingValue) {
        this.restartScrolling();
      }
    },
    script() {
      this.stopScrolling();
      if (!this.hasScript) {
        this.settingsOpen = false;
      }
    },
    scrollSpeed(scrollSpeedValue) {
      this.selectedScrollSpeed = clampScrollSpeed(scrollSpeedValue);
    },
  },
  mounted() {
    this.reducedMotionQuery = getReducedMotionQuery();
    this.reducedMotion = this.reducedMotionQuery?.matches ?? false;
    this.reducedMotionQuery?.addEventListener?.('change', this.handleReducedMotionChange);
  },
  beforeUnmount() {
    this.cancelScrollAnimation();
    this.reducedMotionQuery?.removeEventListener?.('change', this.handleReducedMotionChange);
    this.reducedMotionQuery = null;
  },
  methods: {
    emitTelemetry(event, reason) {
      if (!AUTOCUE_TELEMETRY_REASONS[event]?.includes(reason)) {
        return;
      }

      this.$emit('telemetry', { event, reason });
    },
    toggleVisibility() {
      this.visible = !this.visible;
      if (!this.visible) {
        this.settingsOpen = false;
      }
      this.emitTelemetry('autocue_open', this.visible ? 'opened' : 'hidden');
    },
    setAlignment(value) {
      if (isAlignment(value)) {
        this.selectedAlignment = value;
        this.$emit('update:alignment', value);
        this.emitTelemetry('autocue_alignment', value);
      }
    },
    setFontSize(value) {
      this.selectedFontSize = clampFontSize(value);
      this.$emit('update:fontSize', this.selectedFontSize);
    },
    setScrollSpeed(value) {
      this.selectedScrollSpeed = clampScrollSpeed(value);
      this.$emit('update:scrollSpeed', this.selectedScrollSpeed);
    },
    startScrolling() {
      if (!this.hasScript) {
        this.emitTelemetry('autocue_control_failure', 'no_script');
        return;
      }
      if (this.reducedMotion) {
        this.emitTelemetry('autocue_control_failure', 'reduced_motion');
        return;
      }
      if (!this.$refs.scriptScroller) {
        this.emitTelemetry('autocue_control_failure', 'missing_scroller');
        return;
      }
      const delay = this.scrollState === 'paused' ? 0 : SCROLL_START_DELAY_MS;
      this.cancelScrollAnimation();
      this.scrollState = 'scrolling';
      this.lastTickElapsed = readMonotonicTime() + delay;
      this.scrollAnimationFrame = window.requestAnimationFrame(this.scrollLoop);
    },
    pauseScrolling() {
      if (!this.$refs.scriptScroller) {
        this.emitTelemetry('autocue_control_failure', 'missing_scroller');
        return;
      }
      this.cancelScrollAnimation();
      this.scrollState = 'paused';
    },
    resumeScrolling() {
      this.startScrolling();
    },
    restartScrolling() {
      if (!this.hasScript) {
        this.emitTelemetry('autocue_control_failure', 'no_script');
        return;
      }
      if (this.reducedMotion) {
        this.emitTelemetry('autocue_control_failure', 'reduced_motion');
        this.stopScrolling();
        return;
      }
      if (!this.$refs.scriptScroller) {
        this.emitTelemetry('autocue_control_failure', 'missing_scroller');
        return;
      }
      this.stopScrolling();
      this.startScrolling();
    },
    stopScrolling() {
      this.cancelScrollAnimation();
      this.scrollState = 'stopped';
      this.scrollPosition = 0;
      const scroller = this.$refs.scriptScroller;
      if (scroller) {
        scroller.scrollTop = 0;
      }
    },
    scrollLoop(timestamp) {
      const scroller = this.$refs.scriptScroller;
      if (!scroller) {
        this.cancelScrollAnimation();
        this.scrollState = 'paused';
        this.emitTelemetry('autocue_control_failure', 'missing_scroller');
        return;
      }
      // Keep the first lines still until the initial reading delay has elapsed.
      if (timestamp < this.lastTickElapsed) {
        this.scrollAnimationFrame = window.requestAnimationFrame(this.scrollLoop);
        return;
      }
      const deltaSeconds = Math.min(
        MAX_FRAME_DELTA_SECONDS,
        Math.max(0, (timestamp - this.lastTickElapsed) / 1000),
      );
      this.scrollPosition += this.selectedScrollSpeed * deltaSeconds;
      scroller.scrollTop = this.scrollPosition;
      this.lastTickElapsed = timestamp;
      if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1) {
        this.pauseScrolling();
        return;
      }
      this.scrollAnimationFrame = window.requestAnimationFrame(this.scrollLoop);
    },
    syncScrollPosition() {
      if (this.scrollState === 'scrolling') {
        return;
      }
      const scroller = this.$refs.scriptScroller;
      if (scroller) {
        this.scrollPosition = scroller.scrollTop;
      }
    },
    cancelScrollAnimation() {
      if (this.scrollAnimationFrame !== null) {
        window.cancelAnimationFrame(this.scrollAnimationFrame);
      }
      this.scrollAnimationFrame = null;
    },
    handleReducedMotionChange(event) {
      this.reducedMotion = event.matches;
      if (this.reducedMotion) {
        this.pauseScrolling();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@layer app {
  .guided-autocue {
    background: rgba(var(--v-theme-on-surface), 0.035);
    border: 1px solid rgb(var(--v-theme-tw-surface-accent));
    border-radius: 8px;
    box-sizing: border-box;
    color: rgb(var(--v-theme-on-surface));
    display: flex;
    flex-direction: column;
    inline-size: 100%;
    min-inline-size: 0;
    padding: 6px 10px;
  }
  .guided-autocue__header {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: space-between;
    min-inline-size: 0;
  }
  .guided-autocue__heading { font-size: 0.875rem !important; font-weight: 700; overflow-wrap: anywhere; }
  .guided-autocue__header-actions,
  .guided-autocue__lifecycle { align-items: center; display: flex; gap: 4px; }
  .guided-autocue__header-actions { flex-shrink: 0; }
  .guided-autocue__script {
    border-radius: 4px;
    line-height: 1.4;
    margin-block-start: 4px;
    max-block-size: 96px;
    min-block-size: 0;
    overflow-y: auto;
    padding: 4px 6px;
    scrollbar-color: var(--tw-scrollbar-fg-color) var(--tw-scrollbar-bg-color);
    scrollbar-width: thin;
  }
  .guided-autocue__script-text { overflow-wrap: anywhere; text-align: start; white-space: pre-wrap; }
  .guided-autocue--align-center .guided-autocue__script-text { text-align: center; }
  .guided-autocue--align-left .guided-autocue__script-text { text-align: left; }
  .guided-autocue--align-right .guided-autocue__script-text { text-align: right; }
  .guided-autocue :focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 2px; }
  @media (prefers-reduced-motion: reduce) {
    .guided-autocue__script { scroll-behavior: auto; transition: none; }
  }
}
</style>
